import { fetchPublic, fetchWithAuth } from '@/services/api/apiConfig';
import type { AttributionSubmission, Ga4Identifiers } from '@/utils/attributionCapture';

export type ChatSessionFilter = Record<string, unknown>;
export type ChatChannel = 'web' | 'link';

/**
 * Chat/LLM client.
 */
export interface ChatPayload {
  session_id: string;
  message: string;
  agent_uuid: string;
  channel?: ChatChannel;
  event?: string;
  cal_user_id?: string;
  live_button_id?: string | number;
  preview_button_id?: string | number;
  exact_response?: boolean;
  is_button?: boolean;
  /**
   * SCRUM-1600 redesign: widget-click messages carry a templated
   * message (e.g. `'{{ role_selection }}'`) plus a metadata object whose key
   * matches the agent-emitted token. The widget descriptor under that key
   * carries the click payload (`{type:'click', value, flow_type, parent_chat_id?}`).
   * The BE persists both message and metadata as-is, mirroring the agent-row
   * widget convention; analytics/audit and the FE renderer share one parser.
   */
  metadata?: Record<string, unknown>;
  /** IANA zone from the browser; sent automatically by sendChatMessage when available. */
  client_time_zone?: string;
  /**
   * Flow Builder "Test your flow" kickoff (preview agents only). Names the exact flow
   * version the builder has open so the test pane opens straight on its first node,
   * skipping flow selection / the role picker. Honored by the FB2 runtime on the first
   * turn; ignored for live agents.
   */
  flow_v2_kickoff?: {
    flow_uuid: string;
    flow_type: string;
    role: string;
  };
  /**
   * Secure-link handoff kickoff (ALI-2320): this turn was fired by the page on load,
   * not typed by the visitor. The backend answers it normally (role picker) but
   * persists no user row, so the synthetic message never appears in the transcript,
   * the operator inbox, or exports.
   */
  handoff_kickoff?: boolean;
}

export type TerminalEventType = 'meeting_scheduled' | 'visitor_ended' | 'inactivity_timeout';

export interface FeedbackSubmission {
  rating: number | null;
  message?: string | null;
  source: string;
  raw_message?: string | null;
  node_uuid: string;
  chat_history_id: number;
  submitted_at: string;
  skipped: boolean;
}

export interface TerminalEventMeeting {
  id: string;
  scheduled_at: string;
}

export interface TerminalEvent {
  type: TerminalEventType;
  timestamp: string;
  meeting?: TerminalEventMeeting;
}

export interface ChatHistoryMessage {
  /** alita_core ChatHistory.id — stable per row, used to anchor widget-selection stitch. */
  id?: number;
  sender: string;
  message: string;
  created_at: string;
  hidden: boolean;
  is_button: boolean;
  /**
   * Single, generalizable JSONField shared with the BE (alita_core ChatHistory.metadata).
   * Discriminated on shape:
   *   - `{ presentation: { kind }, source: { ... } }` → stable render contract + provenance
   *   - `{ type: 'cal_response', ... }`             → CalCom payload (agent rows)
   *   - `{ <widgetToken>: { type: 'options' | ... }, ... }` → widget defs (agent rows, SCRUM-1600)
   *   - `{ choice_value, choice_type, parent_chat_id? }` → role-selection click (user rows, SCRUM-1600)
   */
  metadata?: Record<string, unknown> | null;
  /** FB2 feedback submission, when a history endpoint includes denormalized lead/session data. */
  feedback?: FeedbackSubmission | null;
}

export interface ChatHistoryResponse {
  messages: ChatHistoryMessage[];
  terminal_event: TerminalEvent | null;
}

/** Raw captured attribution for the conversation Details panel (ALI-1696). */
export type ConversationAttribution = {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  term: string | null;
  content: string | null;
  /** Superuser-only; omitted for other roles. */
  client_id?: string | null;
  /** Superuser-only; omitted for other roles. */
  session_id?: number | null;
};

export const chatClient = {
  getChatSessions: async (params?: ChatSessionFilter) => {
    const url = `llm/chat/session/`;
    const payload = params || {};
    return fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        timezone: payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });
  },

  getUnreadChatSessionCount: async (
    params?: ChatSessionFilter
  ): Promise<{ unread_count: number }> => {
    const payload = params || {};
    const response = await fetchWithAuth('llm/chat/session/unread-count/', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        timezone: payload.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      }),
    });
    return response?.data ?? { unread_count: 0 };
  },

  getChatHistory: async (sessionId: string): Promise<ChatHistoryResponse> => {
    const response = await fetchWithAuth(
      `llm/chat/history/${sessionId}/`,
      {},
      { allowNonOk: true }
    );
    if (response?.status !== 'success') {
      const errorMsg =
        typeof response?.data === 'string'
          ? response.data
          : response?.data?.error || 'Failed to load chat history';
      throw new Error(errorMsg);
    }

    const data = response?.data;

    // New response shape: { messages: [...], terminal_event: {...} }
    if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.messages)) {
      return {
        messages: data.messages,
        terminal_event: data.terminal_event ?? null,
      };
    }

    // Legacy response shape: data is a flat array of messages
    const legacyPayload = Array.isArray(data) ? data : (data?.results ?? []);
    return {
      messages: Array.isArray(legacyPayload) ? legacyPayload : [],
      terminal_event: null,
    };
  },

  createChatSession: async (payload: {
    agent_uuid: string;
    user_uuid?: string;
    attribution?: AttributionSubmission;
    device_type?: 'mobile' | 'tablet' | 'desktop';
  }) => {
    return fetchWithAuth('llm/chat/session/', {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },

  updateChatSession: async (sessionId: string, payload: Record<string, unknown>) => {
    return fetchWithAuth(`llm/chat/session/${sessionId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },

  renameConversationSession: async (sessionId: string, name: string) => {
    return fetchWithAuth(`llm/chat/session/${sessionId}/rename/`, {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  updateAnonymousSession: async (payload: { anonymous_id: string }) => {
    return fetchWithAuth('llm/chat/session/update-anonymous/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  sendChatMessage: async (payload: ChatPayload) => {
    const clientTimeZone =
      typeof Intl !== 'undefined' && typeof Intl.DateTimeFormat === 'function'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined;
    return fetchWithAuth('llm/chat/response/', {
      method: 'POST',
      body: JSON.stringify({
        ...payload,
        ...(clientTimeZone ? { client_time_zone: clientTimeZone } : {}),
      }),
    });
  },

  endChatSession: async (sessionId: string) => {
    return fetchPublic(`llm/chat/session/${sessionId}/end/`, {
      method: 'POST',
    });
  },

  cancelSessionEnd: async (sessionId: string) => {
    return fetchPublic(`llm/chat/session/${sessionId}/end/`, {
      method: 'DELETE',
    });
  },

  /**
   * Hand over GA4 identifiers the host page only produced after this conversation
   * was created. Fills the attribution row's identifier columns while they are
   * empty and leaves them alone once set, so calling it again is harmless.
   */
  backfillSessionGa4Identifiers: async (sessionId: string, identifiers: Ga4Identifiers) => {
    return fetchPublic(`llm/chat/session/${sessionId}/attribution/ga4/`, {
      method: 'POST',
      body: JSON.stringify(identifiers),
    });
  },

  savePingMessage: async (payload: {
    session_id: string;
    agent_uuid: string;
    user_uuid?: string;
    ping_number: 1 | 2;
  }): Promise<{ data: { id: number; message: string } } | null> => {
    return fetchPublic('llm/chat/reengagement-ping/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  getConversationRating: async (sessionId: string): Promise<number | null> => {
    const response = await fetchWithAuth(`llm/chat/session/${sessionId}/rating/`, {
      method: 'GET',
    });
    return response?.data?.rating ?? null;
  },

  getConversationAttribution: async (
    sessionId: string
  ): Promise<ConversationAttribution | null> => {
    const response = await fetchWithAuth(
      `llm/chat/session/${sessionId}/attribution/`,
      {
        method: 'GET',
      },
      { allowNonOk: true }
    );
    if (response?.status !== 'success' || !response?.data) {
      return null;
    }
    return response.data as ConversationAttribution;
  },

  rateConversation: async (sessionId: string, rating: number | null): Promise<number | null> => {
    const response = await fetchWithAuth(`llm/chat/session/${sessionId}/rating/`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
    return response?.data?.rating ?? null;
  },

  /** Resolve a presigned GET URL for a lead-upload document (SCRUM-1676). */
  getDocumentUrl: async (
    s3Key: string,
    options?: { download?: boolean; filename?: string }
  ): Promise<string | null> => {
    const params = new URLSearchParams({ s3_key: s3Key });
    if (options?.download) {
      params.set('download', 'true');
      if (options.filename) params.set('filename', options.filename);
    }
    try {
      const response = await fetchWithAuth(`llm/chat/document-url/?${params}`);
      return (response?.data?.url as string) ?? null;
    } catch {
      return null;
    }
  },

  /**
   * Resolve a presigned GET URL with scan-status awareness (SCRUM-1772).
   *
   * Unlike `getDocumentUrl`, this method distinguishes between a file that is
   * awaiting a malware scan (`scanPending: true`) and one that is permanently
   * unavailable (`scanPending: false, url: null`), enabling the caller to show
   * a polling/retry UX for the former.
   */
  getDocumentUrlWithStatus: async (
    s3Key: string,
    options?: { download?: boolean; filename?: string }
  ): Promise<{ url: string | null; scanPending: boolean }> => {
    const params = new URLSearchParams({ s3_key: s3Key });
    if (options?.download) {
      params.set('download', 'true');
      if (options.filename) params.set('filename', options.filename);
    }
    try {
      const response = await fetchWithAuth(`llm/chat/document-url/?${params}`);
      return { url: (response?.data?.url as string) ?? null, scanPending: false };
    } catch (err: unknown) {
      const typedErr = err as { status?: number; body?: string };
      // 403 with "processing" in the body means the file is still being scanned.
      // Any other error (404, 403 quarantined, network, etc.) is permanently unavailable.
      if (typedErr.status === 403 && typedErr.body?.includes('processing')) {
        return { url: null, scanPending: true };
      }
      return { url: null, scanPending: false };
    }
  },

  /** Upload a lead-qualification document via the Django proxy to S3 (SCRUM-1675). */
  uploadDocument: async (payload: {
    session_id: string;
    question_id: string;
    file: File;
    /** Signed authorization token from the file_upload widget (SCRUM-1827). */
    upload_token?: string;
  }) => {
    const form = new FormData();
    form.append('session_id', payload.session_id);
    form.append('question_id', payload.question_id);
    form.append('file', payload.file);
    if (payload.upload_token) {
      form.append('upload_token', payload.upload_token);
    }
    return fetchPublic('llm/chat/upload/', {
      method: 'POST',
      body: form,
    });
  },
};
